import { SelectionHeader } from '@/components/bulkQueue/cells/SelectionHeader';
import {
    useAssignmentDateRec,
    useBulkTentativeDateContext,
    useIsEntitySelected,
    useSelectedDate,
} from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateContext';
import { CellProps, HeaderProps } from '@/components/bulkQueue/types';
import { AssignmentDateDecisionSupport } from '@/components/decisionSupport/AssignmentDateDecisionSupport';
import { DateDecisionSupport } from '@/components/decisionSupport/DateDecisionSupportTrigger';
import { DateRecommendationFragment } from '@/components/decisionSupport/queries/dateRecommendations.gql.types';
import {
    DecisionSupportSelection,
    canSelectVisit,
    pickTopRanked,
    selectionCaption,
    useAxleRecommended,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { CaptionLabel } from '@/components/labels/CaptionLabel';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { coerceToDate } from '@/utils/datetimes/javascriptDates';
import { Checkbox } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';

type DateRecommendationsCellProps = {
    isLoading: boolean;
    visitId: string;
    recommendations: DateRecommendationFragment[];
    selectedDate: string | null;
    setSelectedDate(visitId: string, date: string | null): void;
    disabled?: boolean;
};

export const DateRecommendationsCell: FC<DateRecommendationsCellProps> = (props) => {
    const { isLoading, setSelectedDate, visitId, selectedDate, recommendations, disabled } = props;

    const selectedDateRecommendation = useMemo(
        () =>
            recommendations.find(
                (recommendation) => GraphInputHelpers.date(recommendation.date) === selectedDate,
            ) ?? null,
        [recommendations, selectedDate],
    );

    const topRecommendation = useAxleRecommended(recommendations);

    const isSelectionDifferentFromTop =
        selectedDateRecommendation != null &&
        topRecommendation != null &&
        GraphInputHelpers.date(selectedDateRecommendation.date) !==
            GraphInputHelpers.date(topRecommendation.date);

    const onSelectedDate = useCallback(
        (recommendation: DateRecommendationFragment | null) => {
            setSelectedDate(visitId, GraphInputHelpers.date(recommendation?.date ?? null));
        },
        [setSelectedDate, visitId],
    );

    return (
        <VerticalStack>
            <CaptionLabel fontSize={'0.8em'}>
                {isSelectionDifferentFromTop ? 'Selected' : 'Recommended'}
            </CaptionLabel>
            <HorizontalStack spacing={1} sx={{ justifyContent: 'space-between' }}>
                {selectedDateRecommendation ? (
                    <>
                        {selectedDateRecommendation.date.toDateString()}
                        <DateDecisionSupport
                            selectedDateRecommendation={selectedDateRecommendation}
                            setSelectedDate={onSelectedDate}
                            recommendations={recommendations}
                            disabled={disabled}
                        />
                    </>
                ) : (
                    <NoneLabel text={isLoading ? 'Loading...' : 'None'} fontSize={'14px'} />
                )}
            </HorizontalStack>
        </VerticalStack>
    );
};

// Legacy prop-based version (used by UnifiedVisitsWorkflowTable, OrderVisitsWorkflowTable)
type LegacyDateRecommendationsSelectionCellProps = {
    entity: {
        id: string;
    };
    selectedDate: string | null;
    isLoading: boolean;
    isSelected: boolean;
    recommendations: DateRecommendationFragment[];
    isInFlight: boolean;
    disabled?: boolean;
    setSelectedDate(visitId: string, date: string | null): void;
    setIsSelected(id: string, isSelected: boolean): void;
};

export const LegacyDateRecommendationsSelectionCell: FC<
    LegacyDateRecommendationsSelectionCellProps
> = (props) => {
    const {
        entity: { id },
        recommendations,
        isInFlight,
        isLoading,
        setIsSelected,
        isSelected,
        setSelectedDate,
        selectedDate,
        disabled = false,
    } = props;
    const hasRecommendations = recommendations.length > 0;

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <VerticalStack>
                <DateRecommendationsCell
                    recommendations={recommendations}
                    setSelectedDate={setSelectedDate}
                    selectedDate={selectedDate}
                    visitId={id}
                    isLoading={isLoading}
                    disabled={isInFlight || disabled}
                />
            </VerticalStack>
            {hasRecommendations && (
                <Checkbox
                    size='small'
                    disabled={isLoading || isInFlight || disabled}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        setIsSelected(id, !isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};

// DS visitAssignment flow: date recommendations come per-visit from the new
// query; the selected-date string + checkbox/submit are unchanged.
const AssignmentDateSelectionCell: FC<CellProps> = (props) => {
    const {
        entity: { id },
    } = props;

    const { definitions, dateRecommendation } = useAssignmentDateRec(id);
    const [selectedDate, setSelectedDate] = useSelectedDate(id);
    const [isSelected, setIsSelected] = useIsEntitySelected(id);
    const { state, disabled } = useBulkTentativeDateContext();

    const recommendations = useMemo(
        () => dateRecommendation?.recommendations ?? [],
        [dateRecommendation],
    );
    const canSelect = canSelectVisit(recommendations.length > 0, selectedDate != null);

    const isLoading = state === 'loading';

    const topRecommendation = useMemo(() => pickTopRanked(recommendations), [recommendations]);

    // The store holds only the user's pick.
    // fall back to the top rec.
    const resolvedDate =
        selectedDate ?? (topRecommendation ? GraphInputHelpers.date(topRecommendation.date) : null);

    const selectedRow = useMemo(() => {
        if (!resolvedDate) {
            return null;
        }
        const allDates: { date: Date }[] = [
            ...recommendations,
            ...(dateRecommendation?.incompatibleDates ?? []),
        ];
        return allDates.find((r) => GraphInputHelpers.date(r.date) === resolvedDate) ?? null;
    }, [recommendations, dateRecommendation, resolvedDate]);

    const selection: DecisionSupportSelection | null = selectedRow
        ? {
              id: GraphInputHelpers.date(selectedRow.date),
              label: coerceToDate(selectedRow.date).toDateString(),
          }
        : null;

    const setSelection = useCallback(
        (next: DecisionSupportSelection | null) => {
            setSelectedDate(next?.id ?? null);
        },
        [setSelectedDate],
    );

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <VerticalStack>
                <CaptionLabel fontSize={'0.8em'}>
                    {selectionCaption(selectedDate != null)}
                </CaptionLabel>
                <HorizontalStack spacing={1} alignItems='center'>
                    {selectedRow ? (
                        coerceToDate(selectedRow.date).toDateString()
                    ) : (
                        <NoneLabel text={isLoading ? 'Loading...' : 'None'} fontSize={'14px'} />
                    )}
                    <AssignmentDateDecisionSupport
                        definitions={definitions}
                        dateRecommendation={dateRecommendation}
                        selectedDate={selection}
                        setSelectedDate={setSelection}
                        disabled={state === 'submitting' || disabled}
                    />
                </HorizontalStack>
            </VerticalStack>
            {canSelect && (
                <Checkbox
                    size='small'
                    disabled={isLoading || state === 'submitting' || disabled}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        setIsSelected(!isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};

export const DateSelectionCell: FC<CellProps> = (props) => {
    return <AssignmentDateSelectionCell {...props} />;
};

export const DateSelectionHeader: FC<HeaderProps> = (props) => {
    const { hasEditableItems, ...rest } = props;
    const {
        entityCount,
        summary,
        state,
        selectedCount,
        submit,
        selectableCount,
        selectAll,
        disabled,
    } = useBulkTentativeDateContext();
    return (
        <SelectionHeader
            {...rest}
            hasItems={entityCount > 0}
            hasEditableItems={hasEditableItems}
            isLoading={state === 'loading'}
            isSubmitting={state === 'submitting'}
            isDisabled={state !== 'idle' || selectedCount === 0}
            disabled={disabled}
            numSelected={selectedCount}
            summaryString={summary}
            onApply={submit}
            selectableCount={selectableCount}
            onSelectAll={selectAll}
        />
    );
};
